import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Wordmark } from "@/components/Wordmark";
import { Mascot } from "@/components/Mascot";
import { ArrowLeft, ArrowRight, BookOpen } from "lucide-react";

export default function BlogListPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/blog");
        setPosts(data.posts || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen text-[#FAF0DE] relative overflow-hidden">
      <div className="mesh-bg" />

      <header className="relative z-10 max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="blog-logo">
          <Mascot pose="newspaper" className="w-10 h-10" />
          <Wordmark className="h-5" text="#FAF0DE" accent="#FF8C42" />
        </Link>
        <Link to="/" className="text-xs font-mono uppercase tracking-widest text-[#FAF0DE]/60 hover:text-white flex items-center gap-2">
          <ArrowLeft className="w-3 h-3" /> Accueil
        </Link>
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-6 pb-24">
        <div className="mb-16 max-w-3xl">
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-[#FF8C42] mb-4">/ Blog</div>
          <h1 className="font-display text-5xl md:text-8xl font-black tracking-[-0.03em] leading-[0.88] mb-6" data-testid="blog-title">
            Le <span className="text-shimmer">blog</span>.
          </h1>
          <p className="text-lg text-[#FAF0DE]/70 max-w-xl">
            Analyses, retours d'expérience et deep-dives SEO — publiés au fil de l'eau.
          </p>
        </div>

        {loading ? (
          <div className="text-[#FAF0DE]/40 font-mono text-sm">Chargement…</div>
        ) : posts.length === 0 ? (
          <div className="rounded-3xl border border-[#FAF0DE]/10 bg-[#132038]/60 backdrop-blur p-12 text-center">
            <BookOpen className="w-10 h-10 text-[#FF8C42] mx-auto mb-4" strokeWidth={1.5} />
            <div className="font-display text-2xl font-bold mb-2">Le premier article arrive bientôt.</div>
            <p className="text-[#FAF0DE]/50">En attendant, la veille automatisée tourne 24/7 dans le dashboard des abonnés.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-5">
            {posts.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} data-testid={`blog-post-${p.slug}`}
                className="card-lift group relative overflow-hidden rounded-3xl border border-[#FAF0DE]/10 bg-[#132038]/60 backdrop-blur hover:border-[#FAF0DE]/25">
                {p.cover_image && (
                  <div className="aspect-[16/9] overflow-hidden">
                    <img src={p.cover_image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                )}
                <div className="p-6 md:p-8">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#FAF0DE]/40 mb-3">
                    {(p.published_at || "").slice(0, 10)}
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl font-black leading-tight mb-4 group-hover:text-[#FFD600] transition-colors">
                    {p.title}
                  </h3>
                  <p className="text-sm text-[#FAF0DE]/60 leading-relaxed line-clamp-3 mb-4">{p.excerpt}</p>
                  {p.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {p.tags.slice(0, 4).map((t) => (
                        <span key={t} className="text-[10px] font-mono text-[#FAF0DE]/50 border border-[#FAF0DE]/10 rounded-full px-2 py-0.5">#{t}</span>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[#FAF0DE]/40 group-hover:text-[#FF8C42] transition-colors">
                    Lire <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
